import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { useParamState } from '@app/util/hooks/useParamState'
import { HousingFormPageOverview } from './sections/HousingFormPageOverview'
import { HousingFormPagePlots } from './sections/HousingFormPagePlots'
import { HousingFormPageInitiatives } from './sections/HousingFormPageInitiatives'
import { housingFormTabs, housingFormTabsQueryParamName } from './constants'
import { Thread } from '@app/components/blocks/thread/Thread'
import { ViewHousingFormModel } from 'functions/app/housing-form/view'
import useMediaQuery from '@app/util/useMediaQuery'
import { HousingFormPagePopularHousingForms } from './sections/HousingFormPagePopularHousingForms'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { HOUSING_FORM_FEATURES } from '@schema/featureToggle'
import { isFeatureEnabled } from '@app/util/featureToggle'
import { HousingFormPageExperts } from './sections/HousingFormPageExperts'

const isKnowledgeHubTabAvailable = (
  housingForm: ViewHousingFormModel,
): boolean => {
  return (
    (housingForm.knowledgeHubModules?.length ?? 0) > 0 &&
    isFeatureEnabled(
      housingForm.featureToggles,
      HOUSING_FORM_FEATURES.knowledgeHub,
    )
  )
}

const isExpertsTabAvailable = (housingForm: ViewHousingFormModel): boolean => {
  return (
    (housingForm.serviceProviders?.length ?? 0) > 0 &&
    isFeatureEnabled(housingForm.featureToggles, HOUSING_FORM_FEATURES.experts)
  )
}

export const TabContent = ({
  housingForm,
}: {
  housingForm: ViewHousingFormModel
}) => {
  const t = useTranslations().app.housingFormPage.tabs
  const isMobile = useMediaQuery()
  const showKnowledgeHubTab = isKnowledgeHubTabAvailable(housingForm)
  const showExpertsTab = isExpertsTabAvailable(housingForm)

  const [tab, setTab] = useParamState(
    housingFormTabs,
    housingFormTabs.overview,
    housingFormTabsQueryParamName,
  )

  return (
    <Column alignItems="stretch" gap="2rem" style={{ width: '100%' }}>
      <TabsGroup
        selectedTab={tab}
        onSelectTab={setTab}
        tabs={[
          { label: t.overview, value: housingFormTabs.overview },
          { label: t.thread, value: housingFormTabs.thread },
          { label: t.initiatives, value: housingFormTabs.initiatives },
          { label: t.plots, value: housingFormTabs.plots },
          {
            label: t.knowledgeHub,
            value: housingFormTabs.knowledgeHub,
            isVisible: showKnowledgeHubTab,
          },
          {
            label: t.experts,
            value: housingFormTabs.experts,
            isVisible: showExpertsTab,
          },
          {
            label: t.popularHousingForms,
            value: housingFormTabs.popularHousingForms,
            isVisible: isMobile,
          },
        ]}
      />

      {match(tab)
        .with(housingFormTabs.overview, () => (
          <HousingFormPageOverview housingForm={housingForm} />
        ))
        .with(housingFormTabs.thread, () =>
          housingForm.thread ? (
            <Thread key="thread" threadId={housingForm.thread.id} />
          ) : null,
        )
        .with(housingFormTabs.initiatives, () => (
          <HousingFormPageInitiatives housingForm={housingForm} />
        ))
        .with(housingFormTabs.plots, () => (
          <HousingFormPagePlots housingForm={housingForm} />
        ))
        .with(housingFormTabs.experts, () =>
          showExpertsTab ? (
            <HousingFormPageExperts housingForm={housingForm} />
          ) : null,
        )
        .with(housingFormTabs.knowledgeHub, () =>
          showKnowledgeHubTab ? (
            <KnowledgeHubModulesSection
              modules={housingForm.knowledgeHubModules ?? []}
            />
          ) : null,
        )
        .with(housingFormTabs.popularHousingForms, () => (
          <HousingFormPagePopularHousingForms excludeIds={[housingForm.id]} />
        ))
        .exhaustive()}
    </Column>
  )
}
